import { useState, useEffect, useCallback } from "react";
import { useSubscription, useQuery } from "@apollo/client";
import {
  ON_START_MESSAGE_USAGE_UPDATED,
  GET_START_MESSAGE_ANALYTICS,
} from "../services/graphql/start-message-operations";
import type { StartMessage } from "../types/start-message-types";

interface UsageStats {
  totalUsage: number;
  smsUsage: number;
  chatUsage: number;
  recentActivity: Array<{
    timestamp: Date;
    type: "SMS" | "CHAT";
    deviceType?: string;
  }>;
  lastUpdated: Date | null;
}

interface UseRealTimeUsageStatsOptions {
  messageId?: string;
  enableSubscriptions?: boolean;
  maxRecentActivity?: number;
}

interface UseRealTimeUsageStatsResult {
  usageStats: UsageStats;
  loading: boolean;
  error: string | null;
  isRealTimeActive: boolean;
  refreshStats: () => void;
}

export function useRealTimeUsageStats(
  options: UseRealTimeUsageStatsOptions = {}
): UseRealTimeUsageStatsResult {
  const {
    messageId,
    enableSubscriptions = true,
    maxRecentActivity = 10,
  } = options;

  const [usageStats, setUsageStats] = useState<UsageStats>({
    totalUsage: 0,
    smsUsage: 0,
    chatUsage: 0,
    recentActivity: [],
    lastUpdated: null,
  });

  const [isRealTimeActive, setIsRealTimeActive] = useState<boolean>(false);

  // Query for initial analytics data
  const {
    data: analyticsData,
    loading: analyticsLoading,
    error: analyticsError,
    refetch: refetchAnalytics,
  } = useQuery(GET_START_MESSAGE_ANALYTICS, {
    variables: {
      messageId,
      dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0], // Last 30 days
      dateTo: new Date().toISOString().split("T")[0],
    },
    skip: !messageId,
    errorPolicy: "all",
    onCompleted: (data) => {
      if (data?.getStartMessageAnalytics) {
        const analytics = data.getStartMessageAnalytics;
        setUsageStats((prev) => ({
          ...prev,
          totalUsage: analytics.totalUsage,
          smsUsage: analytics.smsUsage,
          chatUsage: analytics.chatUsage,
          lastUpdated: new Date(),
        }));
      }
    },
  });

  // Subscription for real-time usage updates
  const { data: subscriptionData, error: subscriptionError } = useSubscription(
    ON_START_MESSAGE_USAGE_UPDATED,
    {
      variables: { messageId },
      skip: !messageId || !enableSubscriptions,
      onData: ({ data }) => {
        if (data?.data?.onStartMessageUsageUpdated) {
          const updatedMessage: StartMessage =
            data.data.onStartMessageUsageUpdated;

          setUsageStats((prev) => {
            // Add to recent activity
            const newActivity = {
              timestamp: new Date(),
              type: "SMS" as const, // Default to SMS, could be enhanced with more data
            };

            const updatedActivity = [newActivity, ...prev.recentActivity].slice(
              0,
              maxRecentActivity
            );

            return {
              ...prev,
              totalUsage: updatedMessage.usageCount,
              recentActivity: updatedActivity,
              lastUpdated: new Date(),
            };
          });

          setIsRealTimeActive(true);

          // Reset real-time indicator after a delay
          setTimeout(() => {
            setIsRealTimeActive(false);
          }, 2000);
        }
      },
      onError: (error) => {
        console.error("Subscription error:", error);
        setIsRealTimeActive(false);
      },
    }
  );

  // Add simulated activity for demonstration
  const addSimulatedActivity = useCallback(
    (type: "SMS" | "CHAT", deviceType?: string) => {
      setUsageStats((prev) => {
        const newActivity = {
          timestamp: new Date(),
          type,
          deviceType,
        };

        const updatedActivity = [newActivity, ...prev.recentActivity].slice(
          0,
          maxRecentActivity
        );

        const increment = type === "SMS" ? { smsUsage: 1 } : { chatUsage: 1 };

        return {
          ...prev,
          totalUsage: prev.totalUsage + 1,
          ...increment,
          recentActivity: updatedActivity,
          lastUpdated: new Date(),
        };
      });

      setIsRealTimeActive(true);
      setTimeout(() => {
        setIsRealTimeActive(false);
      }, 2000);
    },
    [maxRecentActivity]
  );

  // Manual refresh function
  const refreshStats = useCallback(() => {
    if (messageId) {
      refetchAnalytics();
    }
  }, [messageId, refetchAnalytics]);

  // Simulate real-time updates for demonstration (remove in production)
  useEffect(() => {
    if (!messageId || !enableSubscriptions) return undefined;

    const interval = setInterval(() => {
      // Randomly simulate activity (remove in production)
      if (Math.random() < 0.1) {
        // 10% chance every 5 seconds
        const activityType = Math.random() < 0.6 ? "SMS" : "CHAT";
        const deviceTypes = ["PHONE", "DESKTOP", "TABLET"];
        const deviceType =
          deviceTypes[Math.floor(Math.random() * deviceTypes.length)];
        addSimulatedActivity(activityType, deviceType);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [messageId, enableSubscriptions, addSimulatedActivity]);

  const loading = analyticsLoading;
  const error = analyticsError?.message || subscriptionError?.message || null;

  return {
    usageStats,
    loading,
    error,
    isRealTimeActive,
    refreshStats,
  };
}
